import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { FooterButtonType, MessageSenderType } from '../../../interfaces';
import { faArrowUp, faKeyboard, faPlus, faRightToBracket } from '@fortawesome/free-solid-svg-icons';
import { FooterAddonComponent } from '../../molecules';
import { MessageKind } from '@clientwire/api';
import { ButtonComponent, FilesToUpload, InputComponent, MessageComponent } from '@akostm/orbit-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Overlay } from '@angular/cdk/overlay';
import { Dialog } from '@angular/cdk/dialog';
import { TakePhotoOverlayComponent } from '../../templates';
import { logger, SharedUtils } from 'libs/shared/src/utils';
import { ActiveConversationStore } from 'libs/shared/src/stores';
import { ConversationHelperService } from 'libs/shared/src/services';
import { faUnlock } from '@fortawesome/pro-solid-svg-icons';
import { MessageTemplateLight, ConversationTypeTemplateKind } from '@clientwire/api';

@Component({
  selector: 'shared-conversation-footer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    InputComponent,
    FooterAddonComponent,
    MessageComponent,
    TranslatePipe,
  ],
  templateUrl: './conversation-footer.component.html',
  styleUrl: './conversation-footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ConversationFooterComponent {
  readonly arrowUpIcon = faArrowUp;
  readonly addonButtonIcon = signal(faPlus);
  readonly unlockIcon = faUnlock;
  readonly joinIcon = faRightToBracket;
  readonly addonBarVisible = signal(false);
  readonly deletedAttachment = signal<MessageKind | null>(null);
  readonly #defaultController = new FormControl<string | null>(null);
  readonly messageSenderTypeEnum = MessageSenderType;
  readonly messageInput = viewChild(InputComponent);

  //#region Inputs
  readonly senderType = input.required<MessageSenderType>();
  readonly conversationId = input.required<string>();
  readonly templates = input<Map<string, MessageTemplateLight> | undefined>();
  readonly isConversationLocked = input(false);
  readonly isConversationArchived = input(false);
  readonly conversationLockNote = input<string>('SHARED.CONVERSATION_LOCKED.DEFAULT');
  readonly nextMessageSourceId = input<string>();
  readonly messageInputController = input<FormControl>(this.#defaultController);
  readonly loading = input<boolean>(false);
  readonly userId = input<string | null>();
  readonly userIsNotParticipant = input<boolean>();
  readonly noUsers = input<boolean>();
  //endregion

  //#region Outputs
  readonly unlockAction = output<string>();
  readonly sendMessage = output<boolean>();
  readonly isTyping = output<boolean>();
  readonly joinConversation = output<void>();
  //endregion

  //#region Models
  readonly files = model<FilesToUpload[]>();
  //#endregion

  //#region Services
  readonly #overlay = inject(Overlay);
  readonly #dialog = inject(Dialog);
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #translateService = inject(TranslateService);
  //#endregion

  toggleFooterAddon() {
    this.addonBarVisible.update((value) => !value);
    this.addonButtonIcon.set(this.addonBarVisible() ? faKeyboard : faPlus);
  }

  hideFooterAddon() {
    this.addonBarVisible.set(false);
    this.addonButtonIcon.set(faPlus);
  }

  handleAddonClick(type: FooterButtonType) {
    this.hideFooterAddon();

    switch (type) {
      case FooterButtonType.WebCamera: {
        this.#capturePhoto();
        break;
      }
      case FooterButtonType.Location: {
        this.#getLocation();
        break;
      }

      case FooterButtonType.MsgLocation: {
        const template = this.templates()?.get(ConversationTypeTemplateKind.RequestLocation);
        const text = template?.texts[this.#translateService.currentLang] ?? '';

        this.#conversationHelperService.createNewMessage(
          this.conversationId(),
          'REQUEST_LOCATION',
          this.nextMessageSourceId(),
          text
        );
        break;
      }

      default: {
        logger.debug(type);
        break;
      }
    }
  }

  sendMessageHandler(event: boolean) {
    this.hideFooterAddon();
    this.sendMessage.emit(event);
  }

  addonButtonStyle() {
    if (this.addonButtonIcon() === faPlus) {
      return 'addon-button-plus';
    }
    return 'addon-button-keyboard';
  }

  handleDeleteAttachment(fileKind: MessageKind) {
    this.deletedAttachment.set(fileKind);
  }

  #capturePhoto() {
    const positionStrategy = this.#overlay.position().global().centerHorizontally().bottom();
    const dialogRef = this.#dialog.open(TakePhotoOverlayComponent, {
      width: '61.25rem',
      panelClass: 'location-dialog',
      data: {
        location,
      },
      positionStrategy,
    });

    dialogRef.componentInstance?.sendPhoto.subscribe(async (value) => {
      if (value) {
        const file = SharedUtils.dataURIToBlob(value);
        if (!file) throw new Error('No file found');
        const dateNow = new Date().getTime();
        this.#activeConversationStore.updateConversation(this.conversationId(), {
          files: [{ file: new File([file], `${dateNow}.png`, { type: 'image/png' }), src: value }],
        });
      }
      dialogRef.close();
    });
  }

  #getLocation() {
    this.#openLocationSelectorOverlay();
  }

  #openLocationSelectorOverlay(): void {
    this.#conversationHelperService.openLocationSelectorOverlay(
      this.conversationId(),
      this.nextMessageSourceId()
    );
  }
}
